import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import Remove from '@mui/icons-material/Remove';
import SplitscreenOutlined from '@mui/icons-material/SplitscreenOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import { lighten } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { OrderUnitOfMeasure } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { RTU_WAITING_TIMEOUT_IN_MS } from '~utils/rtu/rtu-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
interface SplitJobDialogProps {
  isOpen: boolean;
  job: Nullable<Job>;
  onClose: () => void;
}

interface SplitRow {
  id: number;
  quantity: number;
}

const MIN_ROWS = 1;
const INITIAL_SPLIT_COUNT = 2;

function distributeQuantity(total: number, count: number): number[] {
  const intPart = Math.floor(total);
  const fracPart = +(total - intPart).toFixed(2);

  const base = Math.floor(intPart / count);
  const remainder = intPart % count;

  const result = Array.from({ length: count }, (_, i) => base + (i < remainder ? 1 : 0));

  if (fracPart > 0) {
    result[0] = +(result[0] + fracPart).toFixed(2);
  }

  return result;
}

function createInitialRows(totalQuantity: number): SplitRow[] {
  const quantities = distributeQuantity(Number(totalQuantity), INITIAL_SPLIT_COUNT);

  return quantities.map((qty, i) => ({ id: i + 1, quantity: qty }));
}

export default function SplitJobDialog({ isOpen, job, onClose }: SplitJobDialogProps) {
  const theme = useTheme();
  const { userStore, toasterStore, ordersDispatchStore } = useStores();
  const { splitJob, subscribeToSplitJobResult, isUpdating } = useJob();
  const isLoad = job?.unitOfMeasure?.name === OrderUnitOfMeasure.LOAD;
  const [rows, setRows] = useState<SplitRow[]>(() =>
    createInitialRows(Number(job?.quantity ?? 0)),
  );
  const [nextId, setNextId] = useState(INITIAL_SPLIT_COUNT + 1);

  useEffect(() => {
    if (isOpen && job?.quantity !== null && job?.quantity !== undefined) {
      setRows(createInitialRows(Number(job.quantity)));
      setNextId(INITIAL_SPLIT_COUNT + 1);
    }
  }, [isOpen, job?.id]);

  const totalQuantity = Number(job?.quantity ?? 0);
  const unitOfMeasure = job?.unitOfMeasure?.name ?? '';
  const materialName = job?.material?.name ?? '';

  const totalAssigned = useMemo(
    () => +rows.reduce((sum, r) => sum + (r.quantity || 0), 0).toFixed(2),
    [rows],
  );
  const remaining = +(totalQuantity - totalAssigned).toFixed(2);
  const isValid =
    remaining === 0 &&
    rows.length >= INITIAL_SPLIT_COUNT &&
    rows.every((r) => r.quantity > 0);

  const canAddRow = rows.length < totalQuantity;

  const handleSplitCountChange = useCallback(
    (value: string) => {
      const count = parseInt(value, 10);
      if (!count || count < 1 || count > totalQuantity) return;

      const quantities = distributeQuantity(totalQuantity, count);

      setRows(quantities.map((qty, i) => ({ id: i + 1, quantity: qty })));
      setNextId(count + 1);
    },
    [totalQuantity],
  );

  const handleUpdateQuantity = useCallback(
    (id: number, value: string) => {
      const parsed = isLoad ? parseInt(value, 10) || 0 : parseFloat(value) || 0;
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, quantity: parsed } : r)));
    },
    [isLoad],
  );

  const handleAddRow = useCallback(() => {
    if (!canAddRow) return;
    setRows((prev) => [...prev, { id: nextId, quantity: 0 }]);
    setNextId((prev) => prev + 1);
  }, [nextId, canAddRow]);

  const handleDecrementRows = useCallback(() => {
    if (rows.length <= MIN_ROWS) return;
    setRows((prev) => prev.slice(0, -1));
  }, [rows.length]);

  const handleDistributeEvenly = useCallback(() => {
    const quantities = distributeQuantity(totalQuantity, rows.length);
    setRows((prev) => prev.map((r, i) => ({ ...r, quantity: quantities[i] })));
  }, [rows.length, totalQuantity]);

  const handleRemoveRow = useCallback(
    (id: number) => {
      if (rows.length <= MIN_ROWS) return;
      setRows((prev) => prev.filter((r) => r.id !== id));
    },
    [rows.length],
  );

  const handleSplit = useCallback(async () => {
    if (!job || !isValid) return;

    const companyId = getEffectiveUserCompanyId(userStore);
    const quantities = rows.map((r) => r.quantity);
    const correlationId = await splitJob(job.id, companyId, quantities);

    if (correlationId) {
      const jobId = job.id;
      ordersDispatchStore.orderJobUpdateStart(jobId);

      const timeout = setTimeout(() => {
        ordersDispatchStore.orderJobUpdateEnd(jobId);
      }, RTU_WAITING_TIMEOUT_IN_MS);

      subscribeToSplitJobResult(correlationId, (data) => {
        clearTimeout(timeout);
        ordersDispatchStore.orderJobUpdateEnd(jobId);

        const failedCount = data.failed_ids?.length ?? 0;

        if (failedCount > 0) {
          toasterStore.push(
            alert(t('error_messages.jobs.failed_to_split'), AlertTypes.error),
          );
        } else {
          toasterStore.push(
            alert(t('dispatch.job.split_job.success'), AlertTypes.success),
          );
        }
      });

      onClose();
    }
  }, [
    job,
    isValid,
    rows,
    splitJob,
    subscribeToSplitJobResult,
    userStore,
    toasterStore,
    ordersDispatchStore,
    onClose,
  ]);

  const handleClose = useCallback(() => {
    if (!isUpdating) {
      onClose();
    }
  }, [isUpdating, onClose]);

  if (!job) {
    return null;
  }

  const allocationColor =
    remaining === 0
      ? theme.brandV2.colors.treadGreen
      : remaining < 0
        ? theme.brandV2.colors.treadRed
        : theme.brandV2.colors.treadYellowDark;

  const allocationBg = lighten(allocationColor, 0.9);
  const allocationBorder = lighten(allocationColor, 0.7);

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <Dialog isOpen={isOpen} onClose={handleClose} maxWidth="sm">
        <DialogHeader onClose={handleClose}>
          {t('dispatch.job.split_job.title')}
        </DialogHeader>

        <DialogContent>
          <Typography
            variant="body2"
            color={theme.brandV2.colors.treadGray2}
            sx={{ mb: 2 }}
          >
            {t('dispatch.job.split_job.description')}
          </Typography>

          {/* Source job card */}
          <Box
            sx={{
              background: theme.brandV2.colors.treadGray8,
              border: `1px solid ${theme.brandV2.colors.treadGray7}`,
              borderRadius: 2,
              px: 1.5,
              py: 1.25,
              mb: 2.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '15px',
                }}
              >
                {job.jobId}
              </Typography>
              {materialName && (
                <Typography
                  color={theme.brandV2.colors.treadGray3}
                  sx={{
                    fontSize: '13px',
                  }}
                >
                  {materialName}
                </Typography>
              )}
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                color={theme.brandV2.colors.treadGray3}
                sx={{
                  fontSize: '13px',
                }}
              >
                {t('dispatch.job.split_job.total_quantity')}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '22px',
                }}
              >
                {totalQuantity}{' '}
                <Typography
                  component="span"
                  color={theme.brandV2.colors.treadGray3}
                  sx={{
                    fontSize: '13px',
                  }}
                >
                  {unitOfMeasure}
                </Typography>
              </Typography>
            </Box>
          </Box>

          {/* Split count controls */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                color={theme.brandV2.colors.treadGray3}
                sx={{
                  fontSize: '13px',
                }}
              >
                {t('dispatch.job.split_job.into')}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: `1px solid ${theme.brandV2.colors.treadGray7}`,
                  borderRadius: 1,
                }}
              >
                <IconButton
                  size="small"
                  onClick={handleDecrementRows}
                  disabled={rows.length <= MIN_ROWS}
                  sx={{ p: 0.25 }}
                >
                  <Remove sx={{ fontSize: '16px' }} />
                </IconButton>

                <InputBase
                  type="number"
                  value={rows.length}
                  onChange={(e) => handleSplitCountChange(e.target.value)}
                  slotProps={{ input: { min: 1, max: totalQuantity } }}
                  sx={{
                    width: 32,
                    '& input': {
                      textAlign: 'center',
                      fontSize: '13px',
                      p: 0,
                    },
                  }}
                />

                <IconButton
                  size="small"
                  onClick={handleAddRow}
                  disabled={!canAddRow}
                  sx={{ p: 0.25 }}
                >
                  <Add sx={{ fontSize: '16px' }} />
                </IconButton>
              </Box>

              <Typography
                color={theme.brandV2.colors.treadGray3}
                sx={{
                  fontSize: '13px',
                }}
              >
                {t('dispatch.job.split_job.jobs_label')}
              </Typography>
            </Box>

            <SmallButton
              variant="outlined"
              color="secondary"
              onClick={handleDistributeEvenly}
              startIcon={<SplitscreenOutlined sx={{ fontSize: '12px' }} />}
              sx={{
                '&.MuiButtonBase-root': {
                  fontSize: '11px',
                  height: 28,
                  px: 1,
                },
              }}
            >
              {t('dispatch.job.split_job.distribute_evenly')}
            </SmallButton>
          </Box>

          {/* Quantity table */}
          <Box
            sx={{
              border: `1px solid ${theme.brandV2.colors.treadGray7}`,
              borderRadius: 2,
              mb: 2.5,
              height: 240,
              overflow: 'auto',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.75,
                background: theme.brandV2.colors.treadGray8,
                borderBottom: `1px solid ${theme.brandV2.colors.treadGray7}`,
              }}
            >
              <Typography
                color={theme.brandV2.colors.treadGray3}
                sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  flex: 1,
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                }}
              >
                {t('form_fields.job')}
              </Typography>
              <Typography
                color={theme.brandV2.colors.treadGray3}
                sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                }}
              >
                {t('dispatch.job.split_job.quantity')}
                {unitOfMeasure ? ` (${unitOfMeasure})` : ''}
              </Typography>
            </Box>

            {/* Rows */}
            {rows.map((row, i) => (
              <Box
                key={row.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  pl: 1.5,
                  pr: 0.5,
                  py: 0.5,
                  borderBottom:
                    i < rows.length - 1
                      ? `1px solid ${theme.brandV2.colors.treadGray7}`
                      : 'none',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                    }}
                  >
                    {i === 0
                      ? job.jobId
                      : `${t('dispatch.job.split_job.new_job_label')} ${i}`}
                  </Typography>
                  {i === 0 && (
                    <Typography
                      color={theme.brandV2.colors.treadGray4}
                      sx={{
                        fontSize: '11px',
                      }}
                    >
                      {t('dispatch.job.split_job.original')}
                    </Typography>
                  )}
                </Box>

                <TextField
                  type="number"
                  size="small"
                  value={row.quantity || ''}
                  onChange={(e) => handleUpdateQuantity(row.id, e.target.value)}
                  placeholder="0"
                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: isLoad ? 1 : 'any',
                      style: { textAlign: 'right', padding: '4px 8px' },
                    },
                  }}
                  sx={{
                    width: 60,
                    '& .MuiOutlinedInput-root': { fontSize: '13px' },
                  }}
                />

                <IconButton
                  size="small"
                  onClick={() => handleRemoveRow(row.id)}
                  disabled={rows.length <= MIN_ROWS}
                  sx={{
                    color: theme.brandV2.colors.treadGray4,
                    p: 0.25,
                  }}
                >
                  <Close sx={{ fontSize: '14px' }} />
                </IconButton>
              </Box>
            ))}
          </Box>

          {/* Summary bar */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              py: 1.5,
              background: allocationBg,
              border: `1px solid ${allocationBorder}`,
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box>
                <Typography
                  color={theme.brandV2.colors.treadGray4}
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                  }}
                >
                  {t('dispatch.job.split_job.assigned')}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                  }}
                >
                  {totalAssigned}{' '}
                  <Typography
                    component="span"
                    color={theme.brandV2.colors.treadGray4}
                    sx={{
                      fontSize: '12px',
                    }}
                  >
                    {unitOfMeasure}
                  </Typography>
                </Typography>
              </Box>
              <Box>
                <Typography
                  color={theme.brandV2.colors.treadGray4}
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                  }}
                >
                  {t('dispatch.job.split_job.remaining')}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                  }}
                >
                  {remaining}{' '}
                  <Typography
                    component="span"
                    color={theme.brandV2.colors.treadGray4}
                    sx={{
                      fontSize: '12px',
                    }}
                  >
                    {unitOfMeasure}
                  </Typography>
                </Typography>
              </Box>
            </Box>

            <Typography
              color={allocationColor}
              sx={{
                fontSize: '13px',
                fontWeight: 500,
              }}
            >
              {remaining === 0
                ? t('dispatch.job.split_job.fully_allocated')
                : remaining < 0
                  ? t('dispatch.job.split_job.over_allocated')
                  : t('dispatch.job.split_job.under_allocated')}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <SmallButton
            variant="outlined"
            color="secondary"
            disabled={isUpdating}
            onClick={handleClose}
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="brandV2Orange"
            variant="contained"
            loading={isUpdating}
            disabled={!isValid}
            onClick={handleSplit}
          >
            {t('dispatch.job.split_job.split')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
